/* eslint-disable @typescript-eslint/no-explicit-any */
/*
Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License.
*/
import { nothing } from 'lit';
import { AsyncDirective, directive } from 'lit/async-directive.js';
/* c8 ignore next 6 */
const defaultListener = [
    '',
    () => {
        return;
    },
];
/**
 * Performantly manage listening to event in a series, like:
 *   - `input[type="range"]`: input, input, etc. => change
 *   - `sp-color-area`: pointerdown => pointermove, pointermove, etc. => pointerup
 * Lazily bind events to the specific part of the series while
 * throttling streamed events to 1/frame.
 */
class StreamingListenerDirective extends AsyncDirective {
    constructor() {
        super(...arguments);
        this.start = defaultListener;
        this.streamInside = defaultListener;
        this.end = defaultListener;
        this.streamOutside = defaultListener;
        this.state = 'off';
        this.handleStart = (event) => {
            this.callHandler(this.start[1], event);
            if (event.defaultPrevented) {
                return;
            }
            this.removeListeners();
            this.addListeners('on');
        };
        this.handleStream = (event) => {
            this.callHandler(this.streamInside[1], event);
        };
        this.handleEnd = (event) => {
            this.callHandler(this.end[1], event);
            this.removeListeners();
            this.addListeners('off');
        };
        this.handleBetween = (event) => {
            this.callHandler(this.streamOutside[1], event);
        };
    }
    /* c8 ignore next 4 */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    render(_configGroup) {
        return nothing;
    }
    update(part, [{ start, end, streamInside = defaultListener, streamOutside = defaultListener, },]) {
        var _a;
        if (this.element !== part.element) {
            this.element = part.element;
            this.removeListeners();
        }
        this.host =
            ((_a = part.options) === null || _a === void 0 ? void 0 : _a.host) || this.element;
        this.start = start;
        this.end = end;
        this.streamInside = streamInside;
        this.streamOutside = streamOutside;
        this.addListeners();
    }
    addListeners(state) {
        this.state = state || this.state;
        if (this.state === 'off') {
            this.addListener(this.streamOutside[0], this.handleBetween);
            this.addListener(this.start[0], this.handleStart);
        }
        else if (this.state === 'on') {
            this.addListener(this.streamInside[0], this.handleStream);
            this.addListener(this.end[0], this.handleEnd);
        }
    }
    callHandler(value, event) {
        if (typeof value === 'function') {
            value.call(this.host, event);
        }
        else {
            value.handleEvent(event);
        }
    }
    addListener(type, fn) {
        if (Array.isArray(type)) {
            type.map((eventName) => {
                this.element.addEventListener(eventName, fn);
            });
        }
        else {
            this.element.addEventListener(type, fn);
        }
    }
    removeListener(type, fn) {
        if (Array.isArray(type)) {
            type.map((eventName) => {
                this.element.removeEventListener(eventName, fn);
            });
        }
        else {
            this.element.removeEventListener(type, fn);
        }
    }
    removeListeners() {
        this.removeListener(this.start[0], this.handleStart);
        this.removeListener(this.streamInside[0], this.handleStream);
        this.removeListener(this.end[0], this.handleEnd);
        this.removeListener(this.streamOutside[0], this.handleBetween);
    }
    disconnected() {
        this.removeListeners();
    }
    reconnected() {
        this.addListeners();
    }
}
export const streamingListener = directive(StreamingListenerDirective);
//# sourceMappingURL=streaming-listener.js.map